import { client } from "../gqlClient";
import {
  CREATE_PROGRAM,
  DELETE_PROGRAM,
  GET_LIST_PROGRAMS,
  GET_ONE_PROGRAM,
  PUBLISH_PROGRAM,
  UNPUBLISH_PROGRAM,
  UPDATE_PROGRAM,
} from "./schema";

function transformProgramData(program) {
  if (!program) {
    return null;
  }
  return {
    ...program,
    id: program.slug,
  };
}

export async function programCreate(params) {
  const { data } = params;

  // Build blocks input from the structure data
  let blocksInput;
  if (data.blocks) {
    blocksInput = buildBlocksInput(data.blocks, data.sections, data.classes);
  }

  const input = {
    title: data.title,
    slug: data.slug,
    level: data.level,
    style: data.style,
    categories: data.categories,
    instructorSlug: data.instructorSlug,
    instructorName: data.instructorName,
    isFree: data.isFree,
    publishDate: data.publishDate,
    averageSessionTime: data.averageSessionTime,
    content: data.content,
    relatedClasses: data.relatedClasses,
    ...(blocksInput ? { blocks: blocksInput } : {}),
  };

  const res = await client.mutate(CREATE_PROGRAM, { input });

  return {
    data: transformProgramData(res.adminProgramCreate),
  };
}

export async function getListPrograms(params) {
  const data = await client.query(
    GET_LIST_PROGRAMS,
    null,
    "adminProgramGetList"
  );

  let programs = (data || []).map(transformProgramData);

  // Client-side filtering (Firebase RTDB has no query support)
  const filter = params?.filter;
  if (filter) {
    if (filter.title) {
      const q = filter.title.toLowerCase();
      programs = programs.filter(
        (p) => p.title && p.title.toLowerCase().includes(q)
      );
    }
    if (filter.slug) {
      const q = filter.slug.toLowerCase();
      programs = programs.filter(
        (p) => p.slug && p.slug.toLowerCase().includes(q)
      );
    }
    if (filter.instructorName) {
      const q = filter.instructorName.toLowerCase();
      programs = programs.filter(
        (p) => p.instructorName && p.instructorName.toLowerCase().includes(q)
      );
    }
  }

  // Client-side sorting
  const sort = params?.sort;
  if (sort?.field) {
    const { field, order } = sort;
    programs.sort((a, b) => {
      const aVal = a[field] || "";
      const bVal = b[field] || "";
      if (aVal < bVal) { return order === "ASC" ? -1 : 1; }
      if (aVal > bVal) { return order === "ASC" ? 1 : -1; }
      return 0;
    });
  }

  return {
    data: programs,
    total: programs.length,
  };
}

export async function getOneProgram({ id }) {
  const program = await client.query(
    GET_ONE_PROGRAM,
    { slug: id },
    "adminProgramGetOne"
  );

  return {
    data: transformProgramData(program),
  };
}

export async function programUpdate({ id, data }) {
  const slug = id || data.slug || data.id;

  // Build blocks input from the structure data
  let blocksInput;
  if (data.blocks) {
    blocksInput = buildBlocksInput(data.blocks, data.sections, data.classes);
  }

  const res = await client.mutate(UPDATE_PROGRAM, {
    input: {
      slug,
      title: data.title,
      level: data.level,
      style: data.style,
      categories: data.categories,
      instructorSlug: data.instructorSlug,
      instructorName: data.instructorName,
      isFree: data.isFree,
      publishDate: data.publishDate,
      averageSessionTime: data.averageSessionTime,
      content: data.content,
      relatedClasses: data.relatedClasses,
      ...(blocksInput ? { blocks: blocksInput } : {}),
    },
  });

  return {
    data: transformProgramData(res.adminProgramUpdate),
  };
}

export async function programPublish(params) {
  const slug = params.id;

  const res = await client.mutate(PUBLISH_PROGRAM, { slug });

  return {
    data: transformProgramData(res.adminProgramPublish),
  };
}

export async function programUnPublish(params) {
  const slug = params.id;

  const res = await client.mutate(UNPUBLISH_PROGRAM, { slug });

  return {
    data: transformProgramData(res.adminProgramUnPublish),
  };
}

export async function programDelete(params) {
  const slug = params.id;

  await client.mutate(DELETE_PROGRAM, { slug });

  return { data: { id: slug } };
}

/**
 * Convert the flat blocks/sections/classes maps from Firebase
 * into the nested blocks input format expected by the mutation.
 */
function buildBlocksInput(blocksObj, sectionsObj, classesObj) {
  if (!blocksObj) {
    return undefined;
  }

  // Sort blocks by sortIndex
  const sortedBlocks = Object.values(blocksObj).sort(
    (a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)
  );

  return sortedBlocks.map((block) => {
    const sectionRefIds = block.sections || [];
    const sections = sectionRefIds
      .map((sectionRefId) => sectionsObj?.[sectionRefId])
      .filter(Boolean)
      .map((section) => ({
        title: section.title,
        refId: section.refId,
        classes: (section.classes || []).map((cls) => ({
          classId: cls.id,
          refId: cls.refId,
        })),
      }));

    return {
      title: block.title,
      superscript: block.superscript || undefined,
      refId: block.refId,
      sections,
    };
  });
}
